import type { Rassegna, RassegnaEdition } from '@sse/schema';
import { Plus, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { btnPrimary, btnSecondary } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatRange } from '../lib/format';
import { useSession } from '../lib/session';

export type EditionState = RassegnaEdition['status'] | 'none';

export interface RassegnaRow extends Rassegna {
  edition: Omit<RassegnaEdition, 'status'> & { status: EditionState };
  members: number;
}

export const EDITION_LABEL: Record<EditionState, string> = {
  none: 'Non ancora proposta',
  proposed: 'Proposta, da decidere',
  active: 'Attiva',
  skipped: 'Saltata quest’anno',
};

const EDITION_STYLE: Record<EditionState, string> = {
  none: 'bg-surface text-muted',
  proposed: 'bg-accent text-fg',
  active: 'bg-ok-soft text-fg',
  skipped: 'bg-surface-strong text-muted',
};

export function EditionBadge({ status }: { status: EditionState }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.04em] ${EDITION_STYLE[status]}`}
    >
      {EDITION_LABEL[status]}
    </span>
  );
}

/**
 * Rassegne (docs/07-admin-e-bot.md, "Rassegne"): raccolte di eventi che tornano ogni anno nello
 * stesso periodo. Per ognuna l'edizione di riferimento (in corso, da proporre o la prossima).
 */
export function RassegneList() {
  const { api, canEdit } = useSession();
  const navigate = useNavigate();
  const [rows, setRows] = useState<RassegnaRow[] | null>(null);
  const [today, setToday] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api<{ today: string; rassegne: RassegnaRow[] }>('/rassegne')
      .then((r) => {
        setRows(r.rassegne);
        setToday(r.today);
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));

  useEffect(() => {
    if (canEdit) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  const seed = async () => {
    setBusy(true);
    try {
      const r = await api<{ created: string[] }>('/rassegne/seed', { method: 'POST' });
      setNotice(
        r.created.length
          ? `Aggiunte ${r.created.length} rassegne suggerite.`
          : 'Le rassegne suggerite ci sono già tutte.',
      );
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-[1400px] p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-h2">Rassegne</h1>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => void seed()} disabled={busy} className={btnSecondary}>
            <Sparkles className="size-4" aria-hidden /> Aggiungi le rassegne suggerite
          </button>
          <Link to="/rassegne/new" className={btnPrimary}>
            <Plus className="size-4" aria-hidden /> Nuova rassegna
          </Link>
        </div>
      </div>
      <p className="mt-1 max-w-3xl text-sm text-muted">
        Raccolte di eventi che tornano ogni anno nello stesso periodo. Quando il periodo si avvicina
        il bot chiede nella chat della redazione se attivarla: attivandola, l’AI cerca gli eventi
        già in calendario e poi aggiunge da sola quelli nuovi. Sul sito una rassegna si vede solo
        con l’edizione attiva e durante il suo periodo.
      </p>
      {notice && (
        <p role="status" className="mt-4 bg-surface p-3 text-sm">
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {rows && rows.length === 0 && (
        <p className="mt-6 text-sm">
          Nessuna rassegna. Parti da quelle suggerite (Presepi viventi, Sagre d’autunno, Pizzica e
          Notte della Taranta…) e modificale come vuoi.
        </p>
      )}
      {rows && rows.length > 0 && (
        <div className="relative mt-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm md:min-w-[760px]">
            <thead>
              <tr className="border-b-2 border-fg text-left text-label uppercase tracking-[0.04em] text-muted">
                <th className="py-2 pr-3 font-medium">Rassegna</th>
                <th className="py-2 pr-3 font-medium">Edizione</th>
                <th className="py-2 pr-3 font-medium">Stato</th>
                <th className="py-2 pr-3 font-medium">Eventi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const live =
                  r.edition.status === 'active' &&
                  r.edition.start <= today &&
                  today <= r.edition.end;
                return (
                  <tr
                    key={r.slug}
                    onClick={() => navigate(`/rassegne/${r.slug}?year=${r.edition.year}`)}
                    className={`cursor-pointer border-b border-surface-strong align-top hover:bg-surface ${r.enabled ? '' : 'text-muted'}`}
                  >
                    <td className="py-2 pr-3">
                      <Link
                        to={`/rassegne/${r.slug}?year=${r.edition.year}`}
                        className="font-medium hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.title}
                      </Link>
                      {!r.enabled && <span className="ml-2 text-xs">(spenta)</span>}
                      <div className="hidden text-xs text-muted md:block">{r.intro}</div>
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {formatRange(r.edition.start, r.edition.end)}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <EditionBadge status={r.edition.status} />
                        {live && <span className="text-xs font-medium">sul sito ora</span>}
                      </div>
                    </td>
                    <td className="py-2 pr-3 tabular-nums">{r.members || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
